import { MIP_INVESTOR_INVITATION_FORM } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/constants';
import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import { TextArea } from '@dealstack/frontend/shared/components/forms/TextArea/TextArea';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { CarouselItem } from '@dealstack/frontend/shared/components/fundament/Carousel/CarouselItem';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { usePagination } from '@dealstack/frontend/shared/other/react/hooks/usePagination';
import { GetInvestorDataForSendModal } from '@dealstack/shared/lib/generated';
import { em, rem } from '@mantine/core';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { IButtonV2Props } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2.types';

interface Props {
	onClose: () => void;
	investors: GetInvestorDataForSendModal[];
	invitationForm: UseFormReturn<{ custom_text_field: string }, unknown>;
	onClickDone: () => void;
	onClickInvestorsRemaining: () => void;
	investorsInvited: string[];
	onClickInviteInvestor: (investorId: string) => void;
}

export const ContentView = ({
	onClose,
	investors,
	invitationForm,
	onClickDone,
	onClickInvestorsRemaining,
	onClickInviteInvestor,
	investorsInvited,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const investorRemainingNumber = investors.length - investorsInvited.length;

	const onChangedButtonClick = () => (investorsInvited.length ? onClickDone() : onClose());

	const buttonIcon = investorsInvited.length ? 'thumbs-up' : 'close-v3';

	const buttonText = investorsInvited.length
		? tMipManage('invitation_modal.done')
		: tMipManage('invitation_modal.close');

	const getInvitationText = () => {
		if (investorsInvited.length) {
			return tMipManage('invitation_modal.invite_remaining', {
				investorRemainingNumber: investorRemainingNumber.toString(),
			});
		}

		if (investors?.length > 1) {
			return tMipManage('invitation_modal.invite_all', {
				totalInvestorNumber: investors.length.toString(),
			});
		}

		return tMipManage('invitation_modal.invite');
	};

	const multipleInvitationText = getInvitationText();

	const investorTablePagination = usePagination({
		offset: 0,
		limit: 1,
		totalNumberOfItems: investors.length,
	});
	const investor = investors[investorTablePagination.state.offset];
	const emailTemplate = tMipManage('invitation_modal.email_by_default');

	return (
		<div style={{ padding: '1rem' }}>
			<TopWrapper>
				<HeaderText variant="sans/17/800" disableVariantMapping>
					{tMipManage('invitation_modal.invite_header', {
						totalInvestorNumber: investors.length.toString(),
					})}
				</HeaderText>
			</TopWrapper>
			<FormProvider {...invitationForm}>
				<form>
					<MiddleContentWrapper>
						<CarouselItem
							middle={MiddleContent({
								name: investor?.person?.displayName,
								email: investor?.person?.email,
							})}
							bottom={BottomContent(investor?.linkedContractsName)}
						/>
						<MessageWrapper>
							<Controller
								name={`${MIP_INVESTOR_INVITATION_FORM.CUSTOM_INVITATION_TEXT_AREA}`}
								control={invitationForm.control}
								render={({ field }) => (
									<StyledTextarea
										{...field}
										rows={14}
										fullWidth
										data-testid={
											TEST_ELEMENT_IDS.MIP_MANAGE_SEND_INVITATION_MODAL_MESSAGE_TEXT_AREA
										}
										placeholder={emailTemplate}
									/>
								)}
							/>
						</MessageWrapper>
					</MiddleContentWrapper>
				</form>
			</FormProvider>
			<BottomContentWrapper>
				<div data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_SEND_INVITATION_MODAL_CANCEL}>
					{investors.length > 1 && (
						<ButtonsWrapper>
							<PrevButton onClick={investorTablePagination.onPrevPage} />
							{investorsInvited.includes(investor?.id) ? (
								<ButtonV2
									size="s"
									icon="tick"
									buttonTheme="light"
									variant="secondary"
									disabled
									leftIcon
								>
									{tMipManage('invitation_modal.invited')}
								</ButtonV2>
							) : (
								<ButtonV2
									size="s"
									buttonTheme="light"
									icon="mail"
									variant="secondary"
									leftIcon
									onClick={() => onClickInviteInvestor(investor?.id)}
								>
									{tMipManage('invitation_modal.invite')}
								</ButtonV2>
							)}
							<Text variant="sans/14/400" disableVariantMapping>
								{tMipManage('invitation_modal.investors', {
									currentInvestor: (
										investorTablePagination.currentPage + 1
									).toString(),
									totalInvestorsNumber:
										investorTablePagination.totalPages.toString(),
								})}
							</Text>
							<NextButton onClick={investorTablePagination.onNextPage} />
						</ButtonsWrapper>
					)}
				</div>
				<Wrapper>
					{investorsInvited.length !== investors?.length ? (
						<ModalButtonWrapper>
							<div
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_SEND_INVITATION_MODAL_CANCEL
								}
							>
								<ButtonV2
									size="s"
									icon={buttonIcon}
									rightIcon
									buttonTheme="light"
									variant="ghost"
									onClick={() => onChangedButtonClick()}
								>
									{buttonText}
								</ButtonV2>
							</div>
							<div data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_SEND_INVITATION_MODAL_OK}>
								<ButtonV2
									size="s"
									icon="mail"
									buttonTheme="light"
									variant="primary"
									leftIcon
									onClick={() => onClickInvestorsRemaining()}
								>
									{multipleInvitationText}
								</ButtonV2>
							</div>
						</ModalButtonWrapper>
					) : (
						<ButtonV2
							size="s"
							icon="thumbs-up"
							buttonTheme="light"
							variant="primary"
							rightIcon
							onClick={() => onClickDone()}
						>
							{tMipManage('invitation_modal.done')}
						</ButtonV2>
					)}
				</Wrapper>
			</BottomContentWrapper>
		</div>
	);
};

const HeaderText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
`;

const LinkedContracts = ({ contractNames }: { contractNames: string[] }) => {
	const mappedDocuments = contractNames.map((contractName, index) => {
		return (
			<ContractDocumentCard
				key={index}
				documentID=""
				documentTitle={contractName}
				valuationsDateFontSizeVariant={true}
				disabled={true}
				fileExtension={null}
			>
				<DocumentCard.DropdownMenu />
			</ContractDocumentCard>
		);
	});

	return <ContentWrapper>{mappedDocuments}</ContentWrapper>;
};

const ContractDocumentCard = styled(DocumentCard)`
	${({ disabled }) =>
		disabled &&
		css`
			opacity: 1;
			pointer-events: none;
		`}
`;

const ContentWrapper = styled.div`
	width: 100%;
	max-height: 16rem;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	overflow-y: auto;
	gap: 1rem;
`;

interface IInvestorInfo {
	name: string;
	email: string;
}

const BottomContent = (contractNames?: string[]) => {
	if (!contractNames) {
		return null;
	}
	return <LinkedContracts contractNames={contractNames} />;
};

const MiddleContent = (investorInfo?: IInvestorInfo) => {
	if (!investorInfo) {
		return null;
	}
	return (
		<RowContent>
			<RightMiddleContainer>
				<ContentText variant="sans/14/600" disableVariantMapping fontColor="blue500">
					{investorInfo.name}
				</ContentText>
				<ContentText variant="sans/14/400" disableVariantMapping fontColor="greys500">
					{investorInfo.email}
				</ContentText>
			</RightMiddleContainer>
		</RowContent>
	);
};

const ModalButtonWrapper = styled.div`
	display: flex;
	gap: 1rem;
`;

const StyledTextarea = styled(TextArea)`
	width: 100%;
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	color: ${defaultTheme.v2.color.neutral[600]};

	textarea {
		font-size: ${rem(14)};
		line-height: ${rem(20)};
		border: none;
		&:focus {
			border: none;
		}
		caret-color: ${defaultTheme.v2.color.neutral[600]};
		color: ${defaultTheme.v2.color.neutral[600]};
		background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	}
`;

const TopWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 1em;
`;

const MiddleContentWrapper = styled.div`
	display: flex;
	& > div {
		width: 100%;
	}
	gap: ${em(16)};
	align-items: flex-start;
`;

const MessageWrapper = styled.div`
	background-color: ${({ theme }) => theme.v2.color.neutral[300]};
	border-radius: ${em(8)};
`;

const BottomContentWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin-top: ${em(16)};
`;

const ButtonsWrapper = styled.div`
	display: flex;
	gap: 0.5em;
	align-items: center;
`;

const Wrapper = styled.div`
	margin-left: auto;
`;

const RowContent = styled.div`
	display: flex;
	width: 100%;
	height: 100%;
`;
const RightMiddleContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 50%;
`;
const mapColorToSpecificText: Record<string, string> = {
	blue500: defaultTheme.v2.color.secondary[800],
	greys500: defaultTheme.v2.color.neutral[600],
	greys400: defaultTheme.v2.color.neutral[500],
	greys600: defaultTheme.v2.color.neutral[700],
	greys200: defaultTheme.v2.color.neutral[300],
};
interface IFontColor {
	fontColor?: string;
}
const ContentText = styled(Text)<IFontColor>`
	${({ fontColor = 'black' }) =>
		fontColor &&
		css`
			color: ${mapColorToSpecificText[fontColor]};
		`}
`;

const PrevButton = styled(ButtonV2).attrs<Partial<IButtonV2Props>>({
	icon: 'arrow-left-v3',
	variant: 'tertiary',
	size: 's',
	leftIcon: true,
	buttonTheme: 'light',
})`
	${Icon}.svg {
		margin-right: 0;
	}
`;

const NextButton = styled(ButtonV2).attrs<Partial<IButtonV2Props>>({
	icon: 'arrow-right-v3',
	variant: 'tertiary',
	size: 's',
	rightIcon: true,
	buttonTheme: 'light',
})`
	${Icon}.svg {
		margin-left: 0;
	}
`;
